import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import ModelPicker from "@/components/ModelPicker";
import LanguagePicker from "@/components/LanguagePicker";
import VersionHistory from "@/components/VersionHistory";
import RichEditor from "@/components/RichEditor";
import { fontFamilyForLanguage, isRTL, isNonLatin } from "@/constants/languageFonts";
import { languageLabel } from "@/constants/languages";
import { Sparkles, Trash2, Globe, Save, Languages } from "lucide-react";

function stripHtml(html) {
  if (!html) return "";
  // Regex-based stripping avoids assigning to innerHTML (no XSS surface even on detached nodes).
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export default function ChapterEditor({ chapter, bookId, bookLanguage = "en", onUpdated, onDeleted }) {
  const [title, setTitle] = useState(chapter.title);
  const [content, setContent] = useState(chapter.content || "");
  const [savedAt, setSavedAt] = useState(chapter.updated_at);
  const [status, setStatus] = useState("idle");
  const [provider, setProvider] = useState("anthropic");
  // Per-chapter language (falls back to book.language). Affects AI gen + editor font.
  const [language, setLanguage] = useState(chapter.language || bookLanguage || "en");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiInstruction, setAiInstruction] = useState("");
  const [convertBusy, setConvertBusy] = useState(false);
  const [editorNonce, setEditorNonce] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setTitle(chapter.title);
    setContent(chapter.content || "");
    setSavedAt(chapter.updated_at);
    setLanguage(chapter.language || bookLanguage || "en");
    setStatus("idle");
  }, [chapter.chapter_id]);

  // Persist a chapter-language change as soon as the user picks it.
  const changeLanguage = async (next) => {
    setLanguage(next);
    try {
      const { data } = await api.patch(`/chapters/${chapter.chapter_id}`, { language: next });
      onUpdated && onUpdated(data);
    } catch {
      toast.error("Failed to save language");
    }
  };

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (title === chapter.title && content === chapter.content) return;
    setStatus("saving");
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.patch(`/chapters/${chapter.chapter_id}`, { title, content });
        setSavedAt(data.updated_at);
        setStatus("saved");
        onUpdated && onUpdated(data);
      } catch {
        setStatus("idle");
        toast.error("Auto-save failed");
      }
    }, 900);
    return () => timerRef.current && clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, content]);

  const generate = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/chapter", {
        book_id: bookId,
        chapter_id: chapter.chapter_id,
        instruction: aiInstruction,
        provider,
        language,
      });
      const text = (data.result || "").trim();
      // Convert AI plain-text paragraphs to HTML paragraphs
      const html = text.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
      setContent((prev) => (prev ? prev + html : html));
      toast.success("AI draft appended");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const wordCount = stripHtml(content).trim().split(/\s+/).filter(Boolean).length;

  // Convert romanised/English text in the editor to the chapter's native script
  // using the AI transliterator. Output replaces the editor content; the rich
  // editor remains fully editable so the author can revert individual words.
  const convertToScript = async () => {
    const plain = stripHtml(content).trim();
    if (!plain) {
      toast.error("Type some text first, then click Convert.");
      return;
    }
    if (!isNonLatin(language)) {
      toast.error("Pick a non-English language above first.");
      return;
    }
    setConvertBusy(true);
    try {
      const { data } = await api.post("/ai/transliterate", {
        text: plain,
        language,
        provider,
      });
      const converted = (data.result || "").trim();
      if (!converted) {
        toast.error("Conversion came back empty. Try again.");
        return;
      }
      const html = converted
        .split(/\n{2,}/)
        .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
        .join("");
      setContent(html);
      setEditorNonce((n) => n + 1); // Force Tiptap to re-apply content
      toast.success(`Converted to ${languageLabel(language)} — edit any word as needed.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Conversion failed");
    } finally {
      setConvertBusy(false);
    }
  };

  const saveSnapshot = async () => {
    const label = prompt("Snapshot label (optional):", "");
    if (label === null) return;
    try {
      await api.post(`/chapters/${chapter.chapter_id}/versions`, { label: (label || "").trim() });
      toast.success("Snapshot saved");
    } catch {
      toast.error("Snapshot failed");
    }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8" data-testid="chapter-editor">
      <div className="editor-shell w-full">
        <div className="flex items-center justify-between mb-6 text-xs" style={{ color: "var(--bf-text-muted)" }}>
          <div className="flex items-center gap-3">
            <span data-testid="chapter-word-count">{wordCount.toLocaleString()} words</span>
            <span>•</span>
            <span data-testid="chapter-save-status">
              {status === "saving" ? "Saving…" : status === "saved" ? "All changes saved" : `Last saved ${savedAt ? new Date(savedAt).toLocaleTimeString() : "–"}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={saveSnapshot} data-testid="chapter-snapshot-btn">
              <Save className="w-4 h-4 mr-1.5" /> Save snapshot
            </Button>
            <VersionHistory
              chapterId={chapter.chapter_id}
              onRestored={(updated) => onUpdated && onUpdated(updated)}
            />
            <Button
              variant="ghost" size="sm"
              onClick={() => {
                if (confirm("Delete this chapter?")) onDeleted && onDeleted(chapter);
              }}
              data-testid="chapter-delete-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-1">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="font-serif text-3xl border-0 px-0 focus-visible:ring-0 h-auto py-2 flex-1"
            style={{
              background: "transparent",
              fontFamily: fontFamilyForLanguage(language),
              direction: isRTL(language) ? "rtl" : "ltr",
            }}
            placeholder="Chapter title"
            data-testid="chapter-title-input"
            lang={language}
          />
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Globe className="w-3.5 h-3.5" style={{ color: "var(--bf-text-muted)" }} />
            <LanguagePicker
              value={language}
              onChange={changeLanguage}
              triggerClassName="h-9 w-40 text-xs"
              testid="chapter-language-picker"
            />
          </div>
        </div>
        {/* Language script helper row (only for non-Latin scripts) */}
        {isNonLatin(language) && (
          <div
            className="flex items-center justify-end gap-3 mb-4 text-xs"
            style={{ color: "var(--bf-text-muted)" }}
            data-testid="chapter-script-helper"
          >
            <span className="text-right max-w-md leading-snug">
              Type in {languageLabel(language)} directly, or write in English letters and click
              Convert — individual words remain editable after conversion.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={convertToScript}
              disabled={convertBusy}
              className="flex-shrink-0"
              data-testid="chapter-convert-script-btn"
            >
              <Languages className="w-3.5 h-3.5 mr-1.5" />
              {convertBusy ? "Converting…" : `Convert to ${languageLabel(language).split(" (")[0]}`}
            </Button>
          </div>
        )}

        <div
          style={{
            fontFamily: fontFamilyForLanguage(language),
            direction: isRTL(language) ? "rtl" : "ltr",
          }}
          lang={language}
          data-testid="chapter-editor-body"
        >
          <RichEditor
            value={content}
            onChange={setContent}
            placeholder="Start typing your chapter here…"
            resetKey={`${chapter.chapter_id}-${editorNonce}`}
          />
        </div>
      </div>

      <aside
        className="p-5 rounded-lg h-fit sticky top-24"
        style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}
        data-testid="ai-side-panel"
      >
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4" style={{ color: "var(--bf-accent)" }} />
          <h3 className="font-serif text-xl">AI Co-author</h3>
        </div>
        <div className="space-y-3 mb-4">
          <div>
            <label className="label-eyebrow block mb-2">Model</label>
            <ModelPicker value={provider} onChange={setProvider} />
          </div>
          <div>
            <label className="label-eyebrow block mb-2 flex items-center gap-1.5">
              <Globe className="w-3 h-3" /> Language
            </label>
            <LanguagePicker
              value={language}
              onChange={changeLanguage}
              triggerClassName="w-full"
              testid="ai-language-picker"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Instruction (optional)</label>
            <Textarea
              rows={3}
              placeholder="Draft a tense confrontation scene where Sarah meets her mentor again…"
              value={aiInstruction}
              onChange={(e) => setAiInstruction(e.target.value)}
              data-testid="ai-instruction"
            />
          </div>
          <Button
            onClick={generate}
            disabled={aiBusy}
            className="w-full text-white"
            style={{ background: "var(--bf-primary)" }}
            data-testid="ai-generate-btn"
          >
            {aiBusy ? "Drafting…" : "Draft next chapter"}
          </Button>
        </div>
        <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
          AI output is inserted as paragraphs at the end of this chapter. Use the toolbar to format.
        </p>
      </aside>
    </div>
  );
}
